<script setup lang="ts">
import Button from "primevue/button";
import Chip from "primevue/chip";
import {TimeAllocationCategoryDTO} from '@/stores/timeAllocationCategories.store.ts';
import {useTimeAllocationColors} from '@/composables/useTimeAllocationColors.ts';

defineProps<{
  categories: TimeAllocationCategoryDTO[];
  shouldShowMoreLink: boolean;
  showUnusedCategories: boolean;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  enableCategory: [key: string];
  toggleUnusedCategories: [];
}>();

const { getCategoryColor } = useTimeAllocationColors();
</script>

<template>
  <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 transform translate-y-2"
      enter-to-class="opacity-100 transform translate-y-0"
      leave-active-class="transition-all duration-300 ease-in"
      leave-from-class="opacity-100 transform translate-y-0"
      leave-to-class="opacity-0 transform translate-y-2"
  >
    <div v-if="categories.length > 0 || shouldShowMoreLink" class="mt-4">
      <div class="text-sm font-medium mb-2">Effort & Time allocation</div>
      <div class="flex flex-wrap gap-2">
        <TransitionGroup
            name="chip"
            tag="div"
            class="contents"
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 transform scale-75"
            enter-to-class="opacity-100 transform scale-100"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 transform scale-100"
            leave-to-class="opacity-0 transform scale-75"
            move-class="transition-transform duration-200 ease-out"
        >
          <Chip
              v-for="category in categories"
              :key="`disabled-${category.key}`"
              :label="category.label"
              :disabled="disabled"
              class="cursor-pointer !text-xs transition-colors duration-150"
              :class="disabled ? 'opacity-50' : 'hover:opacity-80'"
              :style="{ 
                backgroundColor: getCategoryColor(category.key), 
                color: '#fff',
                border: `1px solid ${getCategoryColor(category.key)}`
              }"
              @click="!disabled && emit('enableCategory', category.key)"
              v-tooltip.top="category.description"
          />
        </TransitionGroup>
        
        <!-- More/Less link for unused categories -->
        <Button
            v-if="shouldShowMoreLink"
            :label="showUnusedCategories ? 'less...' : 'more...'"
            text
            size="small"
            class="!text-xs !p-1 text-primary-500 hover:text-primary-700"
            @click="emit('toggleUnusedCategories')"
        />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.chip-enter-active,
.chip-leave-active {
  transition: all 0.2s ease;
}

.chip-enter-from,
.chip-leave-to {
  opacity: 0;
  transform: scale(0.75);
}

.chip-move {
  transition: transform 0.2s ease;
}
</style>
